/**
 * PM-6 — TanStack Query mutation for the projects/v1 `project-import` commit
 * surface (`POST /imports/commit`). Mirrors the ai-findings
 * `useStartImportMutation` (start an async, staged per-row job → importId),
 * but the projects feature talks to its own domain via raw `fetch` +
 * `getProjectsBaseUrl()` (the `useImportAnalyze.ts` pattern) rather than the
 * lyse-pinned `useApi()` axios instance.
 *
 * The edited grid values (rows + mapping + confirmed model resolutions)
 * travel in the body (PM-D6 — no re-upload). `allowOverride` is honored only
 * for a `tenant_admin` server-side (PM-D5); a non-admin true value is rejected
 * 422 `override_requires_tenant_admin`.
 *
 * SYNC: projects/v1@2.7.0 (project-import, commitProjectImport).
 */
import { useMutation } from "@tanstack/react-query";
import { getProjectsBaseUrl, useAuthStore } from "@aeriosiq/auth-client";
import type { CommitImportBody, CommitImportResponse } from "./types.js";

export interface ImportFetchError extends Error {
  status: number;
  body: unknown;
}

function authHeaders(): Headers {
  const session = useAuthStore.getState().session;
  const tenant = useAuthStore.getState().tenant;
  const headers = new Headers();
  if (session?.accessToken) {
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  }
  if (tenant?.tenantSlug) {
    headers.set("X-Tenant-ID", tenant.tenantSlug);
  }
  return headers;
}

async function readError(
  path: string,
  res: Response,
): Promise<ImportFetchError> {
  let parsedBody: unknown = null;
  try {
    parsedBody = await res.json();
  } catch {
    // non-JSON error body — status still surfaces
  }
  const err = new Error(
    `import commit failed: ${path} → ${String(res.status)}`,
  ) as ImportFetchError;
  err.status = res.status;
  err.body = parsedBody;
  return err;
}

/**
 * POST /imports/commit — starts the async, staged per-row import. Returns the
 * `importId` (202) to poll via `useImportStatus`.
 */
export function useImportCommitMutation() {
  return useMutation<CommitImportResponse, ImportFetchError, CommitImportBody>({
    mutationFn: async (body) => {
      const headers = authHeaders();
      headers.set("Content-Type", "application/json");
      const path = "/imports/commit";
      const res = await fetch(`${getProjectsBaseUrl()}${path}`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await readError(path, res);
      return (await res.json()) as CommitImportResponse;
    },
  });
}
